<template>
  <div class="card">
    <p class="card__name">{{ product.name }}</p>
    <div class="card__block">
      <svg @click="overwriteWishlist" v-if="route.name !== 'profile-wishlist'" class="card__wishlist" width="50" viewBox="0 0 50 45" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="50" height="45" rx="8" fill="currentColor"/>
        <path
            d="M17.3579 22.7766C16.9417 22.3588 16.6119 21.8614 16.3878 21.3135C16.1637 20.7655 16.0498 20.178 16.0526 19.5851C16.0526 18.386 16.524 17.236 17.3629 16.3881C18.2019 15.5402 19.3398 15.0638 20.5263 15.0638C22.1895 15.0638 23.6421 15.9787 24.4105 17.3404L25 18.2553L25.5895 17.3404C25.9801 16.6479 26.5454 16.0725 27.2277 15.6725C27.91 15.2726 28.6849 15.0626 29.4737 15.0638C30.6602 15.0638 31.7981 15.5402 32.6371 16.3881C33.476 17.236 33.9474 18.386 33.9474 19.5851C33.9474 20.8298 33.4211 21.9787 32.6421 22.7766L25 30.4894L17.3579 22.7766ZM33.3789 23.5319C34.3789 22.5106 35 21.1277 35 19.5851C35 18.1039 34.4178 16.6833 33.3814 15.6359C32.345 14.5884 30.9394 14 29.4737 14C27.6316 14 26 14.9043 25 16.3085C24.4896 15.592 23.8175 15.0089 23.0395 14.6074C22.2614 14.2059 21.3999 13.9977 20.5263 14C19.0606 14 17.655 14.5884 16.6186 15.6359C15.5822 16.6833 15 18.1039 15 19.5851C15 21.1277 15.6211 22.5106 16.6211 23.5319L25 32L33.3789 23.5319Z"
            :fill="isProductInWishlist ? '#e50000' : '#2F2F2F'" />
      </svg>
      <img :src="product.images.edges[0]?.node.image" :alt="product.name" loading="lazy">
    </div>
    <div class="card__bottom">
      <p>€{{ product.price }}</p>
      <ui-button
          class="card__bottom-button"
          v-if="!isProductInCart"
          @click="addProductToCart"
      >
        {{ t('buttons.addToCart') }}
      </ui-button>
      <div class="card__tools" v-else>
        <button class="card__tools-item card__tools-button" @click="removeProductFormCart">-</button>
        <span class="card__tools-item card__tools-count" v-text="'X' + productQuantity" />
        <button class="card__tools-item card__tools-button" @click="addProductToCart">+</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import UiButton from "@/components/ui/ui-button.vue";
import {useI18n} from "vue-i18n";
import {computed} from "vue";
import {useWishlistStore} from "@/stores/wishlist.js";
import translations from "@/core/helpers/translations.js";
import {useRoute, useRouter} from "vue-router";
import {Wishlist} from "@/core/api/wishlist.js";
import {ElNotification} from "element-plus";
import {useAuthStore} from "@/stores/auth.js";
import {useCartStore} from "@/stores/cart.js";
import {Orders} from "@/core/api/orders.js";

const {t} = useI18n()
const route = useRoute()
const router = useRouter()
const wishlistStore = useWishlistStore()
const authStore = useAuthStore()
const cartStore = useCartStore()

const props = defineProps({
  product: Object
})

const user = computed(() => {
  return authStore.user ? authStore.user : null
})
const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const wishlistUuid = computed(() => {
  return wishlistStore.wishlist ? wishlistStore.wishlist.uuid : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder ? cartStore.currentOrder.uuid : null
})

const isProductInCart = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.find((prod) => prod.node.product.uuid === props.product.uuid)
})
const productQuantity = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.filter((prod) => prod.node.product.uuid === props.product.uuid)[0].node.quantity
})
const isProductInWishlist = computed(() => {
  const el = wishlistStore.wishlist.products?.edges.find(
    (el) => el?.node?.uuid === props.product.uuid
  );

  return !!el;
});

const addProductToCart = async () => {
  if (user.value) {
    const isProductInCart = cartStore.currentOrder?.orderProducts?.edges.find((prod) => prod.node.product.uuid === props.product.uuid)

    if (isProductInCart) {
      if (props.product.quantity > productQuantity.value) {
        const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);
        cartStore.setCurrentOrders(res.data.addOrderProduct.order)

        if (res.errors) {
          ElNotification({
            title: t('popup.error'),
            message: res.errors[0].message,
            type: 'error'
          })
        } else {
          ElNotification({
            message: t('popup.addToCart', { product: props.product.name }),
            type: 'success'
          })
        }
      } else {
        ElNotification({
          title: t('popup.error'),
          message: t('popup.addToCartLimit', { quantity: props.product.quantity }),
          type: 'error'
        })
      }
    } else {
      const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);

      if (res.errors) {
        ElNotification({
          title: t('popup.error'),
          message: res.errors[0].message,
          type: 'error'
        })
      } else {
        cartStore.setCurrentOrders(res.data.addOrderProduct?.order)

        ElNotification({
          message: t('popup.addToCart', { product: props.product.name }),
          type: 'success'
        })
      }
    }
  } else {
    await router.push({
      name: 'login',
      params: {
        locale: localStorage.getItem('ngs-locale') || 'en-gb'
      }
    })
  }
}
const removeProductFormCart = async () => {
  if (productQuantity.value === 1) {
    await removeAllProductFormCart()
    return
  }

  const res = await Orders.overwriteCart("removeOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);

  if (!res.errors) {
    cartStore.setCurrentOrders(res.data.removeOrderProduct.order)
  }
}
const removeAllProductFormCart = async () => {
  const res = await Orders.overwriteCart("removeOrderProductsOfAKind", accessToken.value, orderUuid.value, props.product.uuid);
  if (!res.errors) {
    cartStore.setCurrentOrders(res.data.removeOrderProductsOfAKind.order)
  }
}

const overwriteWishlist = async () => {
  if (user.value) {
    if (isProductInWishlist.value) {
      const res = await Wishlist.overwriteWishlist('removeWishlistProduct', accessToken.value, wishlistUuid.value, props.product.uuid)

      if (!res.errors) {
        wishlistStore.wishlist = res.data.removeWishlistProduct.wishlist
      }
    } else {
      const res = await Wishlist.overwriteWishlist('addWishlistProduct', accessToken.value, wishlistUuid.value, props.product.uuid)

      if (res.data.addWishlistProduct) {
        wishlistStore.wishlist = res.data.addWishlistProduct.wishlist
        ElNotification({
          message: t('popup.addToWishlist', { product: props.product.name }),
          type: "success"
        });
      } else {
        ElNotification({
          message: res.errors[0].message,
          type: "error"
        });
      }
    }
  } else {
    await router.push({
      name: 'login',
      params: {
        locale: localStorage.getItem('ngs-locale') || 'en-gb'
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.card {
  width: 220px;
  border-radius: 16px;
  background-color: rgba(255, 255, 255, 0.00);
  box-shadow: 2px 8px 26px 0 rgba(15, 82, 100, 0.07);
  padding: 13px 15px;

  &__name {
    margin-bottom: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #2F2F2F;
    font-size: 16px;
    font-weight: 500;
    line-height: 22.4px;
  }

  &__block {
    position: relative;
    width: 100%;

    & img {
      border-radius: 10px;
      width: 100%;
      object-fit: cover;
    }
  }

  &__wishlist {
    cursor: pointer;
    position: absolute;
    top: 10px;
    right: 10px;

    & rect {
      transition: 0.2s;
      color: $white;
    }

    @include hover {
      & rect {
        color: #e1e1e1;
      }
    }
  }

  &__bottom {
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    & p {
      color: #2F2F2F;
      text-align: center;
      font-size: 18px;
      font-weight: 600;
      line-height: 23.4px;
    }

    &-button {
      width: fit-content;
      padding: 10px 20px;
      font-size: 12px;
    }
  }

  &__tools {
    height: 38px;
    width: fit-content;
    border: 1px solid #E8E8E8;
    border-radius: 5px;
    display: grid;
    grid-template-columns: 1fr 2fr 1fr;

    &-item {
      display: grid;
      place-items: center;
      width: 100%;
      height: 100%;

      color: #2F2F2F;
      text-align: center;
      font-size: 14px;
      font-weight: 400;
      line-height: 25.6px;
    }

    &-count {
      border-left: 1px solid #E8E8E8;
      border-right: 1px solid #E8E8E8;
    }

    &-button {
      width: 20px;
      background-color: transparent;
      cursor: pointer;
      transition: 0.2s;

      @include hover {
        background-color: #E8E8E8;
      }
    }
  }
}
</style>